import * as Application from 'expo-application'
import { Platform } from 'react-native'

import packageJSON from '../../../package.json'

/**
 * Version/build accessors for the **installed binary**.
 *
 * Do not read these from `Constants.expoConfig` — that describes the JS bundle's
 * config, not the app the user actually installed, and it is wrong in two ways
 * that matter here:
 *
 * - `eas.json` uses `appVersionSource: "remote"` with `autoIncrement`, so EAS owns
 *   the build number. `expoConfig.ios.buildNumber` is whatever is checked into
 *   `app.json` (stale, often `1`), never the real `CFBundleVersion`.
 * - After an OTA update, `expoConfig.version` is the *update's* version. Comparing
 *   it against the store version would then decide an upgrade is/isn't needed
 *   based on the bundle rather than the installed app.
 *
 * `expo-application` reads `CFBundleShortVersionString`/`CFBundleVersion` on iOS
 * and `versionName`/`versionCode` on Android, straight from the binary, so it is
 * unaffected by OTA updates and by remote build numbering.
 */

/** `null` on web — expo-application has no native binary to read there. */
export function getNativeAppVersion(): string | null {
  if (Platform.OS === 'web') return null
  return Application.nativeApplicationVersion
}

/** `null` on web. On Android this is the `versionCode`, stringified. */
export function getNativeBuildNumber(): string | null {
  if (Platform.OS === 'web') return null
  return Application.nativeBuildVersion
}

/**
 * Installed app version, falling back to the bundled `package.json` version on web
 * (and if the native value is somehow unavailable) so callers always get a string
 * that `compare-versions` can parse.
 */
export function getAppVersion(): string {
  return getNativeAppVersion() || packageJSON.version
}

/** Installed build number, or `'0'` when there is no native binary to read. */
export function getBuildNumber(): string {
  return getNativeBuildNumber() || '0'
}
